import { com, kotlin } from '@eidu/web'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import getFormContent from './api/form/getFormContent'
import postFormContent from './api/form/postFormContent'
import postForm from './api/form/postForm'
import FormContentIdAndHash from '../domain/form/FormContentIdAndHash'
import { auditLogKeys, formContentKeys } from './queryKeys'
import KtList = kotlin.collections.KtList
import Page = com.eidu.sharedlib.form.definition.Page
import FormContent = com.eidu.sharedlib.form.FormContent

export const ensurePageKeys = (content: KtList<Page>): KtList<Page> =>
  KtList.fromJsArray(
    content
      .asJsReadonlyArrayView()
      .map((page: Page, index: number) =>
        !page.key ? page.copy(page.title, page.items, `page_${Date.now()}_${index}`) : page
      )
  )

type UseFormContentParams = {
  formId: string
  formContentId: string
}

const useFormContent = ({ formId, formContentId }: UseFormContentParams) => {
  const query = useQuery({
    queryKey: formContentKeys.detail(formId, formContentId),
    queryFn: async () => ensurePageKeys(await getFormContent({ formId, formContentId })),
  })
  return {
    formContent: query.data,
    isLoading: query.isLoading,
    error: query.error,
  }
}

export const usePostFormContent = () => {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: ({ formId, content }: { formId: string; content: FormContent }): Promise<FormContentIdAndHash> =>
      postFormContent({ formId, content }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: auditLogKeys.all }),
  })
  return { postContent: mutateAsync, isPending, error }
}

export const usePostForm = () => {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: ({ formId, initialContent }: { formId: string; initialContent: FormContent }) =>
      postForm({ formId, initialContent }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: auditLogKeys.all }),
  })
  return { postForm: mutateAsync, isPending, error }
}

export default useFormContent
